import { Controller } from "@hotwired/stimulus"
import { post } from "@rails/request.js";
import {
  ENGAGEMENT_HEARTBEAT_MS,
  PageEngagementTracker,
  createAnalyticsId,
  getOrCreateAnalyticsSessionId,
} from '../lib/page_engagement_tracker'

export default class extends Controller {
  static values = {
    extraPageViewParams: Object
  }

  initialize() {
    this.processTrackingEvents = this.processTrackingEvents.bind(this);
    this.handleInteraction = this.handleInteraction.bind(this)
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this)
    this.handlePageHide = this.handlePageHide.bind(this)
    this.handleTurboBeforeRender = this.handleTurboBeforeRender.bind(this)
    this.handleEngagementHeartbeat = this.handleEngagementHeartbeat.bind(this)
  }

  connect(){
    let sessionStorage
    try {
      sessionStorage = window.sessionStorage
    } catch (error) {
      sessionStorage = null
    }
    this.sessionId = window.rafaAnalyticsSessionId || getOrCreateAnalyticsSessionId(sessionStorage)
    window.rafaAnalyticsSessionId = this.sessionId

    document.addEventListener('turbo:load', this.processTrackingEvents);
    document.addEventListener('turbo:before-render', this.handleTurboBeforeRender)
    document.addEventListener('visibilitychange', this.handleVisibilityChange)
    document.addEventListener('pointerdown', this.handleInteraction, { passive: true })
    document.addEventListener('keydown', this.handleInteraction)
    document.addEventListener('scroll', this.handleInteraction, { passive: true, capture: true })
    window.addEventListener('pagehide', this.handlePageHide)
    this.engagementHeartbeat = window.setInterval(this.handleEngagementHeartbeat, ENGAGEMENT_HEARTBEAT_MS)
  }

  disconnect() {
    document.removeEventListener('turbo:load', this.processTrackingEvents);
    document.removeEventListener('turbo:before-render', this.handleTurboBeforeRender)
    document.removeEventListener('visibilitychange', this.handleVisibilityChange)
    document.removeEventListener('pointerdown', this.handleInteraction)
    document.removeEventListener('keydown', this.handleInteraction)
    document.removeEventListener('scroll', this.handleInteraction, true)
    window.removeEventListener('pagehide', this.handlePageHide)
    window.clearInterval(this.engagementHeartbeat)
    this.finishCurrentPage('navigation')
  }

  async processTrackingEvents(e){
    const url = e.detail?.url || window.location.href;
    const previousUrl = window.rafaPreviousTrackedUrl || document.referrer || null;
    const pageViewId = createAnalyticsId(window.crypto)
    let path = window.location.pathname;
    try {
      path = new URL(url, window.location.origin).pathname;
    } catch (error) {
      console.error('Error parsing tracked page URL:', error);
    }

    this.startPageEngagement({ pageViewId, path })

    let platformData = { platform: null, platformVersion: null };
    if (navigator.userAgentData) {
      try {
        const highEntropyData = await navigator.userAgentData.getHighEntropyValues(['platform', 'platformVersion']);
        platformData = {
          platform: highEntropyData.platform,
          platformVersion: highEntropyData.platformVersion
        };
      } catch (error) {
        console.error('Error fetching high entropy values:', error);
      }
    }

    let pageViewed = {
      url,
      path,
      page_view_id: pageViewId,
      session_id: this.sessionId,
      referrer: document.referrer || null,
      previous_url: previousUrl,
      user_agent: navigator.userAgent,
      screen_width: window.screen.width,
      screen_height: window.screen.height,
      language: navigator.language || null,
      platform: platformData.platform || navigator.platform || null,
      platform_version: platformData.platformVersion,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };

    const payload = Object.assign({}, pageViewed, this.extraPageViewParamsValue)
    this.sendEvent(payload);
    window.rafaPreviousTrackedUrl = url;
  }

  startPageEngagement({ pageViewId, path }) {
    this.finishCurrentPage('navigation')
    this.engagementTracker = new PageEngagementTracker({
      pageViewId,
      sessionId: this.sessionId,
      path,
      initiallyVisible: !document.hidden,
      onCheckpoint: payload => this.sendEngagementEvent(payload),
    })
  }

  finishCurrentPage(reason) {
    if (!this.engagementTracker) return

    this.engagementTracker.checkpoint(reason)
    this.engagementTracker = null
  }

  handleInteraction() {
    if (!document.hidden) this.engagementTracker?.markInteraction()
  }

  handleVisibilityChange() {
    this.engagementTracker?.setHidden(document.hidden)
  }

  handlePageHide() {
    this.engagementTracker?.checkpoint('pagehide')
  }

  handleTurboBeforeRender() {
    this.finishCurrentPage('navigation')
  }

  handleEngagementHeartbeat() {
    this.engagementTracker?.checkpoint('heartbeat')
  }

  trackWhatsAppFeedback(event) {
    this.sendEvent({
      event_class: 'WhatsAppFeedbackOpened',
      surface: event.currentTarget.dataset.analyticsSurface || 'unknown'
    });
  }

  sendEvent(payload) {
    post(document.body.dataset.eventsUrl, {
      responseKind: 'json',
      body: payload
    });
  }

  sendEngagementEvent(payload) {
    const eventPayload = { event_class: 'PageEngagementCheckpoint', ...payload }
    const urgent = payload.reason !== 'heartbeat'
    const eventsUrl = document.body.dataset.eventsUrl
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    if (urgent && navigator.sendBeacon && csrfToken) {
      try {
        const body = new Blob(
          [JSON.stringify({ ...eventPayload, authenticity_token: csrfToken })],
          { type: 'application/json' }
        )
        if (navigator.sendBeacon(eventsUrl, body)) return
      } catch (error) {
        console.warn('Unable to queue engagement checkpoint beacon:', error)
      }
    }

    this.sendEvent(eventPayload)
  }
}
